<script lang="ts">
	// Formating
	import { format } from 'svelte-i18n';
	// Design system components
	import { Button, Container, Input, Modal, Text } from '@Shelly/shelly-design-system';
	// Helpers
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import {
		getTemplateComponents,
		getTemplateService,
		translateTemplateTitle,
	} from 'src/helpers/xt1_helpers/template_helpers';
	import { deepCopy, getErrorOrInfoMessageForMinAndMaxUnit, sortOn } from 'src/helpers/utilities';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { isSequenceRunning } from 'src/helpers/xt1_helpers/irrigation/irrigation_helpers';
	import IrrigationConfirmModal from './IrrigationConfirmModal.svelte';
	import IrrigationZoneCard from './IrrigationZoneCard.svelte';
	// Types
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { TemplateComponent } from 'src/types/VirtualComponents';
	import type { Device } from 'src/types/Device';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';

	export let device: Device;
	export let useV2 = false;

	let waitForResponse = false;
	let showEditModal = false;
	let selectedZone: number;

	const minDurationValue = 1;
	const maxDurationValue = 120;
	const minAmountValue = 1;
	const maxAmountValue = 100;
	const zones: TemplateComponent[] = sortOn(getTemplateComponents(device, null, /^zone/), 'role');
	let initSettings = null;
	let zonesSettings = null;
	let confirmModal: IrrigationConfirmModal;

	const saveZoneSetting = async () => {
		if (confirmModal && !(await confirmModal.confirm())) return;
		waitForResponse = true;
		const res: SettingRequest = await gen2Request(device, 'Service.SetConfig', {
			id: 0,
			config: { zones: zonesSettings },
		});
		if (res?.isok) {
			showEditModal = false;
			initSettings = zonesSettings;
		}
		onSettingsResponse(res, device);
		waitForResponse = false;
	};

	const getZoneSetting = () => {
		initSettings = getTemplateService(device).settings?.zones || {};
		zonesSettings = deepCopy(initSettings);
	};

	$: minMaxDuration = getErrorOrInfoMessageForMinAndMaxUnit(
		zonesSettings[selectedZone]?.duration || 0,
		minDurationValue,
		maxDurationValue,
		'min',
	);

	$: minMaxWaterAmount = getErrorOrInfoMessageForMinAndMaxUnit(
		zonesSettings[selectedZone]?.water_amount || 0,
		minAmountValue,
		maxAmountValue,
		'mm/m2',
	);

	getZoneSetting();
</script>

<div
	class={classMap({
		'irrigation-container': useV2,
	})}
>
	{#each zones as zone, i}
		{#if /^zone\d$/.test(zone.role)}
			<IrrigationZoneCard index={i}>
				<svelte:fragment slot="main"
					>{translateTemplateTitle(
						initSettings?.[i]?.name || zone.config?.name || zone.role,
					)}</svelte:fragment
				>
				<button
					slot="control"
					type="button"
					aria-label={$format('button_edit', { default: 'Edit' })}
					on:click={() => {
						selectedZone = i;
						getZoneSetting();
						showEditModal = true;
					}}
				>
					<i class="icon fas fa-gear"></i>
				</button>
			</IrrigationZoneCard>
		{/if}
	{/each}
</div>

{#if showEditModal}
	<Modal
		showModal={showEditModal}
		on:close={() => (showEditModal = false)}
		title={translateTemplateTitle(zones[selectedZone]?.config?.name || '')}
		removeScrollPadding
	>
		<svelte:fragment slot="content">
			<Container padding="scrollPaddings">
				<Text element="h2" styleType="h2" class="mb-s">
					{$format('label_name', { default: 'Name' })}
				</Text>
				<Input
					aria-labelledby="input-mz1q0z0"
					class="mb-m"
					size="m"
					icon="fas fa-edit"
					label={$format('text_irrigation_controller_zone_label', {
						default: 'Zone',
					}) +
						' ' +
						(selectedZone + 1)}
					value={zonesSettings[selectedZone]?.name || ''}
					on:input={(e) => {
						zonesSettings[selectedZone].name = e.target['value'];
					}}
					id="input-mz1q0z0"
				/>
				<Text element="h2" styleType="h2" class="mb-xs">
					{$format('text_irrigation_duration_in_minutes', {
						default: 'Duration (in minutes)',
					})}
				</Text>
				<Text element="h2" styleType="body-extra-small" class="mb-s">
					{$format('xt1_duration_info_text', {
						default: 'How long a zone will be watered.',
					})}
				</Text>
				<Input
					aria-labelledby="input-7nz6nfm"
					class="mb-m"
					size="m"
					icon="fas fa-stopwatch"
					value={zonesSettings[selectedZone]?.duration || ''}
					type="number"
					info={minMaxDuration.info}
					error={minMaxDuration.error}
					on:input={(e) => {
						zonesSettings[selectedZone].duration = Number(e.target['value']);
					}}
					id="input-7nz6nfm"
				/>
				<Text element="h2" styleType="h2" class="mb-xs">
					{$format('text_irrigation_water_amount', {
						default: 'Water Amount (in mm/m2)',
					})}
				</Text>
				<Text element="h2" styleType="body-extra-small" class="mb-s">
					{$format('xt1_duration_water_amount_text', {
						default: 'Water quantity used by the connected watering device for one minute.',
					})}
				</Text>
				<Input
					aria-label={$format('input_water_amount', {
						default: 'Water Amount',
					})}
					class="mb-m"
					size="m"
					icon="fas fa-droplet"
					value={zonesSettings[selectedZone]?.water_amount || ''}
					type="number"
					info={minMaxWaterAmount.info}
					error={minMaxWaterAmount.error}
					on:input={(e) => {
						zonesSettings[selectedZone].water_amount = Number(e.target['value']);
					}}
					id="input-ktwcckr"
				/>
			</Container>
		</svelte:fragment>
		<div class="d-flex justify-content-end" slot="footer">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={() => (showEditModal = false)}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</Button>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				responsiveSize={{
					sm: 'm',
				}}
				disabled={minMaxDuration.error || minMaxWaterAmount.error || waitForResponse}
				loading={waitForResponse}
				on:click={saveZoneSetting}
			>
				{$format('button_save', { default: 'Save' })}
			</Button>
		</div>
	</Modal>
{/if}

{#if isSequenceRunning(device)}
	<IrrigationConfirmModal bind:this={confirmModal} />
{/if}

<style>
	button {
		background: #121517;
		color: white;
		cursor: pointer;
		transition: all 0.2s ease;
		font-size: 18px;
		border: none;
	}
	button:hover {
		color: var(--color-primary);
	}

	.irrigation-container {
		background-color: #0e0f10;
		border-radius: 18px;
		padding: 8px;
	}
</style>
